import React, { useContext, useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactGA from "react-ga4";
import styles from "./Leaderboard.module.scss";
import { Context } from "../Store";
import { capitalize } from "../utils/helpers";
import { isMobile } from "react-device-detect";

// Decorative images shown in the side margins of the leaderboard list on desktop.
// top:    pixels from the top of .scrollWrapper for the first cycle.
// gap:    pixels between the image and the leaderboard list edge.
// height: rendered height in pixels.
// The pattern repeats every PATTERN_HEIGHT px to fill the full list length.
const ROW_HEIGHT = 86; // approximate height of each leaderboard row
const PATTERN_HEIGHT = 1600; // vertical span of one full decoration cycle

const DECORATIONS = [
  // { side: "left", src: "./img/hummingbird.png", alt: "hummingbird", top: -140, gap: 0, height: 170 },
  { side: "right", src: "./img/air-balloon-black.png", alt: "hot air balloon", top: 400, gap: 50, height: 100 },
  { side: "left", src: "./img/hummingbird-two.png", alt: "birds", top: 800, gap: -40, height: 120 },
  { side: "right", src: "./img/birds-two.png", alt: "birds", top: 1200, gap: 40, height: 80 },
  { side: "left", src: "./img/birds-one.png", alt: "birds", top: 1600, gap: 40, height: 80 },
];

const MedalGroup = ({ player }) => (
  <>
    <span className={styles.medal}>
      <img src="./img/medal-gold.svg" alt="gold" />
      <span className={styles.medalPoints}>{player.gold}</span>
    </span>
    <span className={styles.medal}>
      <img src="./img/medal-silver.svg" alt="silver" />
      <span className={styles.medalPoints}>{player.silver}</span>
    </span>
    <span className={styles.medal}>
      <img src="./img/medal-bronze.svg" alt="bronze" />
      <span className={styles.medalPoints}>{player.bronze}</span>
    </span>
  </>
);

const PlayerRow = ({ player, index, isExpanded, onToggle, smallRankNums }) => (
  <>
    <div
      className={`${styles.lbRow} ${styles.lbRowClickable} ${isExpanded ? styles.lbRowExpanded : ""} ${index < 3 ? styles[`lbRowRank${index + 1}`] : ""}`}
      onClick={() => onToggle(player.id)}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="39"
        height="47"
        viewBox="0 0 39 47"
        fill="none"
        className={styles.lbRowRankDiamond}
      >
        <path
          d="M37.4473 23.25L19.375 44.9365L1.30176 23.25L19.375 1.5625L37.4473 23.25Z"
          stroke="#D8B464"
          strokeWidth="2"
        />
        <text
          x="19.375"
          y="23.25"
          textAnchor="middle"
          dominantBaseline="central"
          className={`${styles.lbRowRankNum}${smallRankNums ? ` ${styles.lbRowRankNumSm}` : ""}`}
        >
          {index + 1}
        </text>
      </svg>
      <div className={styles.lbRowAvatarWrap}>
        {index === 0 && <img src="./img/gold-crown.svg" alt="gold-crown" className={styles.lbRowMedal} />}
        <img
          className={styles.lbRowAvatar}
          src={player.photo ? `data:image/jpeg;base64, ${player.photo}` : "./img/boy.png"}
          alt={player.name}
        />
      </div>
      <span className={styles.name}>{capitalize(player.name)}</span>
      <div className={styles.medalContainer}>
        <MedalGroup player={player} />
      </div>
      <span className={styles.points}>
        {player.points} <span>PTS</span>
        <span className={`${styles.medalToggleBtn}${isExpanded ? ` ${styles.medalToggleBtnOpen}` : ""}`}>
          <img src="./img/gold-chevron.svg" alt="" />
        </span>
      </span>
    </div>
    <div
      className={`${styles.medalDropdownRow} ${!isExpanded ? styles.medalDropdownRowHidden : ""} ${index < 3 ? styles[`lbRowRank${index + 1}`] : ""}`}
    >
      <span className={styles.medalDropdownLabel}>Medals:</span>
      <MedalGroup player={player} />
    </div>
  </>
);

const Leaderboard = ({ visibleCount = 6 }) => {
  const [state] = useContext(Context);
  const [activeTab, setActiveTab] = useState("podium");
  const [selectedTeam, setSelectedTeam] = useState(null); // type 1 leaderboard drill-down

  const tabsRef = useRef(null);
  const tabSliderRef = useRef(null);

  const isTournament = state.type == "1" || state.type == "3";
  const isTeams = state.type == "1";

  const updateSlider = (tab) => {
    if (!tabsRef.current || !tabSliderRef.current) return;
    const activeBtn = tabsRef.current.querySelector(`[data-tab="${tab}"]`);
    if (!activeBtn) return;
    tabSliderRef.current.style.width = `${activeBtn.offsetWidth - 1}px`;
    tabSliderRef.current.style.transform = `translateX(${activeBtn.offsetLeft}px)`;
  };

  useEffect(() => {
    updateSlider(activeTab);
  }, [activeTab]);

  useEffect(() => {
    updateSlider("podium");
  }, []);

  const { parsedPlayers = [] } = state;

  let trackEvent = (category, action, label = "") => {
    ReactGA.event({ category, action, label });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedTeam(null);
    trackEvent("UI Interaction", `Leaderboard Tab: ${tab}`);
  };

  // ── Top 3 for podium ────────────────────────────────────────────────────────
  // final_result 1–6 are explicit ranks; everything else (or if none set) is sorted by points.
  const buildRankedList = (items) => {
    const ranked = items
      .filter((i) => Number(i.final_result) >= 1 && Number(i.final_result) <= 6)
      .sort((a, b) => Number(a.final_result) - Number(b.final_result));
    const unranked = items
      .filter((i) => !(Number(i.final_result) >= 1 && Number(i.final_result) <= 6))
      .sort((a, b) => (b.points || 0) - (a.points || 0));
    return ranked.length === 0 ? unranked : [...ranked, ...unranked];
  };

  const buildTop3 = (items) => buildRankedList(items).slice(0, 3);

  const top3 = isTournament
    ? isTeams
      ? [...(state.teams || [])].sort((a, b) => (b.points || 0) - (a.points || 0)).slice(0, 3)
      : buildTop3(parsedPlayers)
    : parsedPlayers.slice(0, 3);

  // ── Leaderboard list rows ────────────────────────────────────────────────────
  // type 1: teams list
  // type 3: players sorted by final_result
  // other:  parsedPlayers (sorted by points, existing behaviour)
  const sortedTeams = isTeams
    ? [...(state.teams || [])]
        .filter((t) => state.players.some((p) => p.team_id == t.id))
        .sort((a, b) => (b.points || 0) - (a.points || 0))
    : [];

  const selectedTeamPlayers = selectedTeam
    ? selectedTeam.isAll
      ? [...parsedPlayers].sort(
          (a, b) => b.points - a.points || b.gold - a.gold || b.silver - a.silver || b.bronze - a.bronze,
        )
      : [...(state.players || [])]
          .filter((p) => p.team_id == selectedTeam.id)
          .sort((a, b) => b.points - a.points || b.gold - a.gold || b.silver - a.silver)
    : [];

  const leaderboardRows = isTeams ? selectedTeamPlayers : isTournament ? buildRankedList(parsedPlayers) : parsedPlayers;

  console.log(parsedPlayers);

  const [showAll, setShowAll] = useState(false);
  const displayedRows = showAll ? leaderboardRows : leaderboardRows.slice(0, visibleCount);
  const hasMore = leaderboardRows.length > visibleCount;
  const hasMoreTeams = sortedTeams.length > visibleCount - 1;
  const displayedTeams = showAll ? sortedTeams : sortedTeams.slice(0, visibleCount - 1);
  const seeMoreBtnRef = useRef(null);
  const savedScrollRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [showFade, setShowFade] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpanded = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useLayoutEffect(() => {
    if (savedScrollRef.current !== null) {
      window.scrollTo(0, savedScrollRef.current);
      savedScrollRef.current = null;
    }
  }, [displayedRows.length]);

  const handleToggleShowAll = () => {
    if (!showAll) savedScrollRef.current = window.scrollY;
    setShowAll((v) => !v);
  };

  useEffect(() => {
    if (!isMobile || activeTab !== "leaderboard") return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowFade(scrollTop + clientHeight < scrollHeight - 10);
    };
    handleScroll();
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [activeTab, selectedTeam]);

  // ── Sub-components defined above Leaderboard component ──────────────────────

  // Team row in the teams-list view (type 1 leaderboard)
  const TeamRow = ({ team, index }) => (
    <div
      className={`${styles.lbRow} ${styles.lbTeamRow}`}
      onClick={() => {
        setSelectedTeam(team);
        setShowAll(false);
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="39"
        height="47"
        viewBox="0 0 39 47"
        fill="none"
        className={styles.lbRowRankDiamond}
      >
        <path
          d="M37.4473 23.25L19.375 44.9365L1.30176 23.25L19.375 1.5625L37.4473 23.25Z"
          stroke="#D8B464"
          strokeWidth="2"
        />
        <text x="19.375" y="23.25" textAnchor="middle" dominantBaseline="central" className={styles.lbRowRankNum}>
          {team.final_result || index + 1}
        </text>
      </svg>
      <span className={styles.name}>{team.name}</span>
      <img src="./img/gold-chevron.svg" alt="view team" className={styles.teamRowChevron} />
    </div>
  );

  // All Players row — always first in the teams list
  const AllPlayersRow = () => (
    <div
      className={`${styles.lbRow} ${styles.lbTeamRow} ${styles.lbRowRank1}`}
      onClick={() => {
        setSelectedTeam({ isAll: true, name: "All Players" });
        setShowAll(false);
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="39"
        height="47"
        viewBox="0 0 39 47"
        fill="none"
        className={styles.lbRowRankDiamond}
      >
        <path
          d="M37.4473 23.25L19.375 44.9365L1.30176 23.25L19.375 1.5625L37.4473 23.25Z"
          stroke="#D8B464"
          strokeWidth="2"
        />
        <text x="19.375" y="23.25" textAnchor="middle" dominantBaseline="central" className={styles.lbRowRankNum}>
          ★
        </text>
      </svg>
      <span className={styles.name}>All Players</span>
      <img src="./img/gold-chevron.svg" alt="view all players" className={styles.teamRowChevron} />
    </div>
  );

  // Podium card — works for both player and team objects
  const PodiumCard = ({ item, index, isTeam }) => (
    <div className={`${styles.podiumPlayer} ${styles[`podiumRank${index + 1}`]}`}>
      <svg xmlns="http://www.w3.org/2000/svg" width="52" height="62" viewBox="0 0 52 62" fill="none">
        <path d="M50.6943 31L26 60.4434L1.30469 31L26 1.55566L50.6943 31Z" stroke="#D8B464" strokeWidth="2" />
        <text x="26" y="31" textAnchor="middle" dominantBaseline="central" className={styles.podiumRankNum}>
          {index + 1}
        </text>
      </svg>
      {isTeam ? (
        <div className={`${styles.podiumAvatarWrap} ${styles.podiumTeamAvatarWrap}`}>
          {isMobile
            ? index === 0 && <img src="./img/confetti-mob.png" alt="confetti" className={styles.podiumConfettiMob} />
            : index === 0 && (
                <img src="./img/confetti-desktop.png" alt="confetti" className={styles.podiumConfettiDesktop} />
              )}
          {index === 0 && <img src="./img/gold-crown.svg" alt="gold-crown" className={styles.podiumMedal} />}
          <div className={`${styles.podiumAvatar} ${styles.podiumTeamAvatar}`}>
            <span>{item.name}</span>
          </div>
        </div>
      ) : (
        <div className={styles.podiumAvatarWrap}>
          {isMobile
            ? index === 0 && <img src="./img/confetti-mob.png" alt="confetti" className={styles.podiumConfettiMob} />
            : index === 0 && (
                <img src="./img/confetti-desktop.png" alt="confetti" className={styles.podiumConfettiDesktop} />
              )}
          {index === 0 && <img src="./img/gold-crown.svg" alt="gold-crown" className={styles.podiumMedal} />}
          <div className={styles.podiumAvatar}>
            <img src={item.photo ? `data:image/jpeg;base64, ${item.photo}` : "./img/boy.png"} alt={item.name} />
          </div>
          <p className={styles.podiumName}>{capitalize(item.name)}</p>
        </div>
      )}
      <div className={styles.podiumPointsContainer}>
        <div className={styles.podiumNumber}>{item.points}</div>
        <div className={styles.podiumPoints}>points</div>
        <div className={styles.dartContainer}>
          <img src="./img/dartHorizontal.svg" alt="Dart" width="57" height="13" />
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.lbSection}>
      <div className={styles.lbContent}>
        <div className={styles.cornerDecorations}>
          <img src="./img/hummingbird.png" alt="hummingbird" className={styles.podiumHummingbird} />
          <img src="./img/corner-deco-left.svg" alt="deco" />
          <img src="./img/corner-deco-right.svg" alt="deco" />
        </div>
        <h3>Who's on top</h3>
        <h2>The Leaderboard</h2>

        {/* Tab switcher */}
        <div className={styles.tabsWrapper}>
          <div className={styles.tabs} ref={tabsRef}>
            <span className={styles.tabSlider} ref={tabSliderRef} />
            <button
              className={`${styles.tab} ${activeTab === "podium" ? styles.tabActive : ""}`}
              onClick={() => handleTabChange("podium")}
              data-tab="podium"
            >
              Podium
            </button>
            <button
              className={`${styles.tab} ${activeTab === "leaderboard" ? styles.tabActive : ""}`}
              onClick={() => handleTabChange("leaderboard")}
              data-tab="leaderboard"
            >
              Leaderboard
            </button>
          </div>
        </div>

        {/* Podium view */}
        <div className={`${styles.podium} ${activeTab !== "podium" ? styles.podiumHidden : ""}`}>
          {/* <img src="./img/hummingbird.png" alt="hummingbird" className={styles.podiumHummingbird} /> */}
          {top3.map((item, index) => (
            <PodiumCard key={item.id} item={item} index={index} isTeam={isTeams} />
          ))}
        </div>

        {/* Leaderboard view */}
        <div className={`${styles.scrollWrapper} ${activeTab !== "leaderboard" ? styles.podiumHidden : ""}`}>
          {/* Side decorations — desktop only, repeated to fill the full list height */}
          {!isMobile &&
            (() => {
              const visibleRowCount = isTeams && !selectedTeam ? displayedTeams.length + 1 : displayedRows.length;
              const totalHeight = visibleRowCount * ROW_HEIGHT + 80; // 80 = top container padding
              const cycles = Math.ceil(totalHeight / PATTERN_HEIGHT) + 1;
              const allDecos = [];
              for (let cycle = 0; cycle < cycles; cycle++) {
                DECORATIONS.forEach((d, i) => {
                  if (cycle > 0 && i === 0) return; // hummingbird only appears in first cycle
                  const top = d.top + cycle * PATTERN_HEIGHT;
                  if (top < totalHeight) {
                    allDecos.push({ ...d, top, key: `${cycle}-${i}` });
                  }
                });
              }
              return allDecos.map((d) => (
                <img
                  key={d.key}
                  src={d.src}
                  alt={d.alt}
                  className={`${styles.lbDecoration} ${d.side === "left" ? styles.lbDecorationLeft : styles.lbDecorationRight}`}
                  style={{ top: d.top, height: d.height, "--deco-gap": `${d.gap}px` }}
                />
              ));
            })()}

          {/* Type 1: team drill-down header */}
          {isTeams && selectedTeam && (
            <div className={styles.teamHeader}>
              <button
                className={styles.backBtn}
                onClick={() => {
                  setSelectedTeam(null);
                  setShowAll(false);
                }}
              >
                <img src="./img/gold-chevron.svg" alt="back" className={styles.backChevron} />
                Back
              </button>
              <p>{selectedTeam.name}</p>
            </div>
          )}

          <div
            ref={scrollContainerRef}
            className={`${styles.scrollContainer}${isMobile && (hasMore || (isTeams && !selectedTeam && hasMoreTeams)) ? ` ${styles.scrollContainerCapped}` : ""}`}
          >
            {/* Type 1, no team selected: teams list */}
            {isTeams && !selectedTeam ? (
              <>
                <AllPlayersRow />
                {(isMobile ? sortedTeams : displayedTeams).map((team, index) => (
                  <TeamRow key={team.id} team={team} index={index} />
                ))}
              </>
            ) : (
              (isMobile ? leaderboardRows : displayedRows).map((player, index) => (
                <PlayerRow
                  key={player.id}
                  player={player}
                  index={index}
                  isExpanded={expandedRows.has(player.id)}
                  onToggle={toggleRowExpanded}
                  smallRankNums={leaderboardRows.length >= 100}
                />
              ))
            )}

            {/* Show more/less */}
            {!isMobile && ((isTeams && !selectedTeam && hasMoreTeams) || (!(isTeams && !selectedTeam) && hasMore)) && (
              <button ref={seeMoreBtnRef} className={styles.seeMoreBtn} onClick={handleToggleShowAll}>
                {showAll ? "Show less" : "Show more"}
                <img
                  src="./img/gold-chevron.svg"
                  alt="arrow down"
                  className={`${styles.seeMoreArrow}${showAll ? ` ${styles.seeMoreArrowUp}` : ""}`}
                />
              </button>
            )}
            {!isTeams && <img src="./img/hummingbird.png" alt="hummingbird" className={styles.leaderboardImg} />}
          </div>
          {isMobile && (hasMore || (isTeams && !selectedTeam && hasMoreTeams)) && showFade && (
            <div className={styles.scrollFade} />
          )}
        </div>
        <div className={`${styles.cornerDecorationsBottom} ${styles.cornerDecorations}`}>
          <img src="./img/corner-deco-left.svg" alt="deco" className={styles.left} />
          <img src="./img/corner-deco-right.svg" alt="deco" className={styles.right} />
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
